import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { limitToTwoDecimals } from "../../shared/utils/functions";
import {
  Button,
  Card,
  CardBody,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import * as yup from "yup";
import { IMaterial } from "../../shared/interfaces/material/IMaterial";
import { MaterialService } from "./service";
import Select from "react-select";
import { IMaterialType } from "../../shared/interfaces/material-type/IMaterialType";
import { MaterialTypeService } from "../material-types/service";
import { SupplierService } from "../suppliers/service";
import { ISupplier } from "../../shared/interfaces/supplier/ISupplier";
import ImageDropzone from "../../shared/imageDropzone";
import { EnumsContext } from "../../context/EnumsContext";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const schema = yup.object().shape({
  name: yup.string().required("validation.required"),
  internalName: yup.string().transform((value) => value || undefined).optional(),
  articleNumber: yup.string().transform((value) => value || undefined).optional(),
  recommendedRetailPrice: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  costPrice: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  purchasePrice: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  supplier: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  unit: yup.string().transform((value) => value || undefined).optional(),
  sorting: yup.string().transform((value) => value || undefined).optional(),
  offerText: yup.string().transform((value) => value || undefined).optional(),
  surfaceTreatment: yup.string().transform((value) => value || undefined).optional(),
  width: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  length: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  height: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  unitPerPack: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  ourSalePrice: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  topLayerThickness: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  weight: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  consumption: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
});

const MaterialForm = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingImage, setExistingImage] = useState<any>(null);
  // The image lives outside react-hook-form, so its changes are tracked separately
  const [imageChanged, setImageChanged] = useState(false);
  const { enums } = React.useContext(EnumsContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<IMaterial>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: "",
      internalName: "",
      articleNumber: "",
      recommendedRetailPrice: 0,
      costPrice: 0,
      purchasePrice: 0,
      supplier: undefined,
      unit: "",
      sorting: "",
      offerText: "",
      surfaceTreatment: "",
      materialType: undefined,
      width: undefined,
      length: undefined,
      height: undefined,
      unitPerPack: undefined,
      ourSalePrice: undefined,
      topLayerThickness: undefined,
      weight: undefined,
      consumption: undefined,
    },
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(
    isDirty || imageChanged,
  );

  const [materialTypeOptions, setMaterialTypeOptions] = useState<
    IMaterialType[]
  >([]);
  const [supplierOptions, setSupplierOptions] = useState<
    { value: number; label: string; discount: number }[]
  >([]);

  const handleCalculatePurchasePrice = (targetCostPrice?: number, targetSupplierId?: number) => {
    const cp = targetCostPrice !== undefined ? targetCostPrice : getValues("costPrice");
    const sid = targetSupplierId !== undefined ? targetSupplierId : getValues("supplier");

    if (cp !== undefined) {
      const selectedSupplier = supplierOptions.find(s => s.value === sid);
      const discount = selectedSupplier?.discount || 0;
      const purchasePrice = cp * (1 - discount / 100);
      setValue("purchasePrice", Number(purchasePrice.toFixed(2)));
    }
  };

  useEffect(() => {
    if (!isEdit) return;
    const fetch = async () => {
      try {
        const res: any = await MaterialService.getSingle(+id!);

        // Sanitize data - convert null values to undefined
        const sanitizedData = {
          name: res.name || "",
          internalName: res.internalName || "",
          articleNumber: res.articleNumber || "",
          recommendedRetailPrice: res.recommendedRetailPrice ?? 0,
          costPrice: res.costPrice ?? 0,
          purchasePrice: res.purchasePrice ?? 0,
          supplier: res?.supplier?.id ?? res?.supplier ?? undefined,
          unit: res.unit || "",
          sorting: res.sorting || "",
          offerText: res.offerText || "",
          surfaceTreatment: res.surfaceTreatment || "",
          materialType: res?.materialType?.id ?? res?.materialType ?? undefined,
          width: res.width ?? undefined,
          length: res.length ?? undefined,
          height: res.height ?? undefined,
          unitPerPack: res.unitPerPack ?? undefined,
          ourSalePrice: res.ourSalePrice ?? undefined,
          topLayerThickness: res.topLayerThickness ?? undefined,
          weight: res.weight ?? undefined,
          consumption: res.consumption ?? undefined,
        };

        reset(sanitizedData as IMaterial);

        // Set existing image if available
        if (res?.materialImage?.path) {
          setExistingImage({
            preview: res.materialImage.path,
            name: res.materialImage.file?.nameOriginal || "Existing Image",
            type: "image",
            isExisting: true,
          });
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetch();
  }, [isEdit, id, reset]);

  useEffect(() => {
    // fetch material types (take first 200)
    MaterialTypeService.getAll({ page: 1, perPage: 200 })
      .then((response) => {
        setMaterialTypeOptions(response.data || []);
      })
      .catch(() => {
        // ignore silently for now
      });

    // fetch suppliers
    SupplierService.getAll({ page: 1, perPage: 200 })
      .then((response) => {
        const options = (response.data || []).map((s: ISupplier) => ({
          value: s.id!,
          label: s.name,
          discount: s.specialDiscount || 0,
        }));
        setSupplierOptions(options);
      })
      .catch(() => {
        // ignore silently for now
      });
  }, []);

  const onSubmit = async (data: IMaterial) => {
    try {
      const formData = new FormData();

      // Create body object with all fields
      const bodyData: any = {
        name: data.name,
      };

      // Only include fields that have values (not null, undefined, or empty string)
      // Always sent (null when empty) so an internal name can be cleared again
      bodyData.internalName = data.internalName?.trim() || null;
      if (data.articleNumber !== null && data.articleNumber !== undefined && data.articleNumber !== "") {
        bodyData.articleNumber = data.articleNumber;
      }
      if (data.recommendedRetailPrice !== undefined && data.recommendedRetailPrice !== null) bodyData.recommendedRetailPrice = data.recommendedRetailPrice;
      if (data.costPrice !== undefined && data.costPrice !== null) bodyData.costPrice = data.costPrice;
      if (data.purchasePrice !== undefined && data.purchasePrice !== null) bodyData.purchasePrice = data.purchasePrice;
      if (data.supplier !== null && data.supplier !== undefined) {
        bodyData.supplier = data.supplier;
      }
      if (data.unit !== null && data.unit !== undefined && data.unit !== "") {
        bodyData.unit = data.unit;
      }
      if (data.sorting !== null && data.sorting !== undefined && data.sorting !== "") {
        bodyData.sorting = data.sorting;
      }
      if (data.offerText !== null && data.offerText !== undefined && data.offerText !== "") {
        bodyData.offerText = data.offerText;
      }
      if (data.surfaceTreatment !== null && data.surfaceTreatment !== undefined && data.surfaceTreatment !== "") {
        bodyData.surfaceTreatment = data.surfaceTreatment;
      }
      if (data.materialType !== null && data.materialType !== undefined) {
        bodyData.materialType = data.materialType;
      }
      if (data.width !== undefined && data.width !== null) bodyData.width = data.width;
      if (data.length !== undefined && data.length !== null) bodyData.length = data.length;
      if (data.height !== undefined && data.height !== null) bodyData.height = data.height;
      if (data.unitPerPack !== undefined && data.unitPerPack !== null) bodyData.unitPerPack = data.unitPerPack;
      if (data.ourSalePrice !== undefined && data.ourSalePrice !== null) bodyData.ourSalePrice = data.ourSalePrice;
      if (data.topLayerThickness !== undefined && data.topLayerThickness !== null) bodyData.topLayerThickness = data.topLayerThickness;
      if (data.weight !== undefined && data.weight !== null) bodyData.weight = data.weight;
      if (data.consumption !== undefined && data.consumption !== null) bodyData.consumption = data.consumption;

      // Append body as JSON string
      formData.append("body", JSON.stringify(bodyData));

      // Append image if selected
      if (imageFile) {
        formData.append("image", imageFile);
      }

      if (isEdit) {
        await MaterialService.update(formData, +id!);
      } else {
        await MaterialService.create(formData);
      }
      SuccessToast(t(`material.${isEdit ? "successUpdate" : "successCreate"}`));
      navigate("/materials");
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  {/* Basic Info */}
                  <Col sm={12} md={6}>
                    <Form.Group controlId="name">
                      <Form.Label className="required">
                        {t("general.name")}
                      </Form.Label>
                      <Controller
                        name="name"
                        control={control}
                        render={({
                          field: { onChange, value },
                          fieldState,
                        }) => (
                          <>
                            <Form.Control
                              onChange={onChange}
                              value={value}
                              isInvalid={!!fieldState.error}
                              placeholder={t("general.enterName")}
                            />
                            <Form.Control.Feedback type="invalid">
                              {t(fieldState.error?.message as string)}
                            </Form.Control.Feedback>
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={12} md={6}>
                    <Form.Group controlId="internalName">
                      <Form.Label>{t("general.internalName")}</Form.Label>
                      <Controller
                        name="internalName"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className="form-control"
                            placeholder={t("general.enterInternalName")}
                            {...field}
                            value={field.value ?? ""}
                          />
                        )}
                      />
                      <Form.Text className="text-muted">
                        {t("general.internalNameHint")}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col sm={12} md={3}>
                    <Form.Group controlId="articleNumber">
                      <Form.Label>{t("material.articleNumber")}</Form.Label>
                      <Controller
                        name="articleNumber"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className="form-control"
                            placeholder={t("material.enterArticleNumber")}
                            {...field}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={12} md={3}>
                    <Form.Group controlId="materialType">
                      <Form.Label>{t("material.materialType")}</Form.Label>
                      <Controller
                        name={"materialType"}
                        control={control}
                        render={({ field }) => (
                          <Select
                            classNamePrefix="react-select"
                            className={`react-select-container ${errors?.materialType ? "is-invalid" : ""}`}
                            options={materialTypeOptions.map((item) => ({
                              value: item.id,
                              label: item.name,
                            }))}
                            value={
                              materialTypeOptions
                                .map((item) => ({
                                  value: item.id,
                                  label: item.name,
                                }))
                                .find(
                                  (option) => option.value === field.value,
                                ) || null
                            }
                            onChange={(option) =>
                              field.onChange(option ? option.value : undefined)
                            }
                            placeholder={t("material.selectMaterialType")}
                            isClearable
                          />
                        )}
                      />
                      {errors?.materialType && (
                        <div className="invalid-feedback d-block">
                          {t(errors.materialType?.message as string)}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Supplier & Pricing */}
                  <Col md={3}>
                    <Form.Group controlId="supplier">
                      <Form.Label>{t("material.supplier")}</Form.Label>
                      <Controller
                        name="supplier"
                        control={control}
                        render={({ field }) => (
                          <Select
                            classNamePrefix="react-select"
                            className="react-select-container"
                            options={supplierOptions}
                            value={
                              supplierOptions.find(
                                (option) => option.value === field.value,
                              ) || null
                            }
                              onChange={(option) => {
                                const val = option ? option.value : undefined;
                                field.onChange(val);
                                handleCalculatePurchasePrice(undefined, val);
                              }}
                            placeholder={t("material.selectSupplier")}
                            isClearable
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId="costPrice">
                      <Form.Label>{t("material.costPrice")}</Form.Label>
                      <Controller
                        name="costPrice"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterCostPrice")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(
                                e.target.value,
                              );
                              const val = limited === "" ? undefined : Number(limited);
                              field.onChange(val);
                              handleCalculatePurchasePrice(val, undefined);
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId="purchasePrice">
                      <Form.Label>{t("material.purchasePrice")}</Form.Label>
                      <Controller
                        name="purchasePrice"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterPurchasePrice")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(
                                e.target.value,
                              );
                              field.onChange(
                                limited === "" ? undefined : Number(limited),
                              );
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group controlId="unit">
                      <Form.Label>{t("general.unit")}</Form.Label>
                      <Controller
                        name={"unit"}
                        control={control}
                        render={({ field }) => (
                          <Select
                            classNamePrefix="react-select"
                            className="react-select-container"
                            options={(enums?.adminUnitType || []).map((item: string) => ({
                              value: item,
                              label: t(`unit.${item}`, item),
                            }))}
                            value={
                              (enums?.adminUnitType || [])
                                .map((item: string) => ({
                                  value: item,
                                  label: t(`unit.${item}`, item),
                                }))
                                .find(
                                  (option: any) => option.value === field.value,
                                ) || null
                            }
                            onChange={(option) =>
                              field.onChange(option ? option.value : undefined)
                            }
                            placeholder={t("material.selectUnit")}
                            isClearable
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId="recommendedRetailPrice">
                      <Form.Label>{t("material.recommendedRetailPrice")}</Form.Label>
                      <Controller
                        name="recommendedRetailPrice"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterRecommendedRetailPrice")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(
                                e.target.value,
                              );
                              field.onChange(
                                limited === "" ? undefined : Number(limited),
                              );
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group controlId="ourSalePrice">
                      <Form.Label>{t("material.ourSalePrice")}</Form.Label>
                      <Controller
                        name="ourSalePrice"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterOurSalePrice")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(e.target.value);
                              field.onChange(limited === "" ? undefined : Number(limited));
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>

                  {/* Physical Properties */}
                  <Col md={2}>
                    <Form.Group controlId="width">
                      <Form.Label>{t("material.width")}</Form.Label>
                      <Controller
                        name="width"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterWidth")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(e.target.value);
                              field.onChange(limited === "" ? undefined : Number(limited));
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group controlId="length">
                      <Form.Label>{t("material.length")}</Form.Label>
                      <Controller
                        name="length"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterLength")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(e.target.value);
                              field.onChange(limited === "" ? undefined : Number(limited));
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group controlId="height">
                      <Form.Label>{t("material.height")}</Form.Label>
                      <Controller
                        name="height"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterHeight")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(e.target.value);
                              field.onChange(limited === "" ? undefined : Number(limited));
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>

                  {/* Product Details */}
                  <Col md={2}>
                    <Form.Group controlId="weight">
                      <Form.Label>{t("material.weight")}</Form.Label>
                      <Controller
                        name="weight"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterWeight")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(e.target.value);
                              field.onChange(limited === "" ? undefined : Number(limited));
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group controlId="unitPerPack">
                      <Form.Label>{t("material.unitPerPack")}</Form.Label>
                      <Controller
                        name="unitPerPack"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterUnitPerPack")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(e.target.value);
                              field.onChange(limited === "" ? undefined : Number(limited));
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group controlId="topLayerThickness">
                      <Form.Label>{t("material.topLayerThickness")}</Form.Label>
                      <Controller
                        name="topLayerThickness"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterTopLayerThickness")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(e.target.value);
                              field.onChange(limited === "" ? undefined : Number(limited));
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="consumption">
                      <Form.Label>{t("material.consumption")}</Form.Label>
                      <Controller
                        name="consumption"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder={t("material.enterConsumption")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(e.target.value);
                              field.onChange(limited === "" ? undefined : Number(limited));
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>

                  {/* Text Fields */}
                  <Col md={6}>
                    <Form.Group controlId="sorting">
                      <Form.Label>{t("material.sorting")}</Form.Label>
                      <Controller
                        name="sorting"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            type="text"
                            placeholder={t("material.enterSorting")}
                            {...field}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="surfaceTreatment">
                      <Form.Label>{t("material.surfaceTreatment")}</Form.Label>
                      <Controller
                        name="surfaceTreatment"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            type="text"
                            placeholder={t("material.enterSurfaceTreatment")}
                            {...field}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={12}>
                    <Form.Group controlId="offerText">
                      <Form.Label>{t("material.offerText")}</Form.Label>
                      <Controller
                        name="offerText"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder={t("material.enterOfferText")}
                            {...field}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>

                  {/* Media */}
                  <Col sm={12}>
                    <Form.Group controlId="image">
                      <Form.Label>{t("material.image")}</Form.Label>
                      <ImageDropzone
                        onFileUpload={(file) => {
                          setImageFile(file);
                          setImageChanged(true);
                        }}
                        existingFile={existingImage}
                        deleteHandler={() => {
                          setImageFile(null);
                          setExistingImage(null);
                          setImageChanged(true);
                        }}
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={12} className="d-flex gap-2">
                    <Button type="submit" variant="primary">
                      {t(`material.${isEdit ? "update" : "create"}`)}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => guardedNavigate("/materials")}>
                      {t("button.cancel")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {unsavedChangesModal}
    </React.Fragment>
  );
};

export default MaterialForm;
